// =====================================================
// 👤 USER DASHBOARD - Vista para Usuarios y Técnicos
// =====================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../stores/authStore';
import { ticketService } from '../../services/ticketService';
import { 
  Plus, MessageCircle, Clock, CheckCircle, AlertCircle, 
  TrendingUp, Ticket as TicketIcon, User, FileText, ChevronRight
} from 'lucide-react';

interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  waitingCustomer: number;
  resolved: number;
  closed: number;
}

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useI18n();
  const { user } = useAuthStore();
  
  const [stats, setStats] = useState<TicketStats>({
    total: 0,
    open: 0,
    inProgress: 0,
    waitingCustomer: 0,
    resolved: 0,
    closed: 0
  });
  const [recentTickets, setRecentTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Verificar si es técnico o admin
  const isTechnician = ['admin', 'technician', 'agent', 'support'].includes(user?.role || '');

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    if (!user) {
      console.log('❌ [UserDashboard] No hay usuario disponible');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 [UserDashboard] Cargando datos para:', {
        email: user.email,
        role: user.role,
        isTechnician
      });
      
      // Cargar tickets según rol
      const response = isTechnician 
        ? await ticketService.getTickets() 
        : await ticketService.getUserTickets(user.email);
      
      console.log('📥 [UserDashboard] Tickets recibidos:', response);
      
      const tickets = response.tickets || [];
      
      console.log('✅ [UserDashboard] Total tickets:', tickets.length);
      
      // Calcular estadísticas
      setStats({
        total: tickets.length,
        open: tickets.filter((t: any) => t.status === 'open').length,
        inProgress: tickets.filter((t: any) => t.status === 'in_progress').length,
        waitingCustomer: tickets.filter((t: any) => t.status === 'waiting_user').length,
        resolved: tickets.filter((t: any) => t.status === 'resolved').length,
        closed: tickets.filter((t: any) => t.status === 'closed').length
      });
      
      // Últimos 5 tickets
      setRecentTickets(tickets.slice(0, 5));
      
    } catch (error: any) {
      console.error('❌ [UserDashboard] Error cargando datos:', error);
      setError(error?.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const cardClass = isDark 
    ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' 
    : 'bg-white border-gray-200 hover:bg-gray-50';
  
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const subTextClass = isDark ? 'text-gray-400' : 'text-gray-600';

  const statCards = [
    { 
      title: isTechnician ? 'Total de Tickets' : 'Mis Tickets',
      value: stats.total,
      icon: TicketIcon,
      color: 'blue',
      bgColor: 'bg-blue-500/10',
      iconColor: 'text-blue-400',
      borderColor: 'border-blue-500/20'
    },
    { 
      title: 'Abiertos',
      value: stats.open,
      icon: Clock,
      color: 'amber',
      bgColor: 'bg-amber-500/10',
      iconColor: 'text-amber-400',
      borderColor: 'border-amber-500/20'
    },
    { 
      title: 'En Progreso',
      value: stats.inProgress,
      icon: TrendingUp,
      color: 'purple',
      bgColor: 'bg-purple-500/10',
      iconColor: 'text-purple-400',
      borderColor: 'border-purple-500/20'
    },
    { 
      title: 'Resueltos',
      value: stats.resolved,
      icon: CheckCircle,
      color: 'green',
      bgColor: 'bg-green-500/10',
      iconColor: 'text-green-400',
      borderColor: 'border-green-500/20'
    }
  ];

  const quickActions = [
    {
      label: 'Crear Ticket',
      icon: Plus,
      onClick: () => navigate('/dashboard/tickets/create'),
      color: 'blue',
      bgColor: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    },
    {
      label: 'Mis Tickets',
      icon: TicketIcon,
      onClick: () => navigate('/dashboard/tickets'),
      color: 'purple',
      bgColor: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600'
    },
    {
      label: 'Asistente técnico IA',
      icon: MessageCircle,
      onClick: () => navigate('/dashboard/chat'),
      color: 'green',
      bgColor: 'bg-green-500',
      hoverColor: 'hover:bg-green-600'
    },
    {
      label: 'Documentación',
      icon: FileText,
      onClick: () => navigate('/dashboard/docs'),
      color: 'orange',
      bgColor: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600'
    }
  ];

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { label: string; class: string } } = {
      'open': { label: 'Abierto', class: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      'in_progress': { label: 'En Progreso', class: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
      'waiting_user': { label: 'Esperando', class: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
      'resolved': { label: 'Resuelto', class: 'bg-green-500/10 text-green-400 border-green-500/20' },
      'closed': { label: 'Cerrado', class: 'bg-gray-500/10 text-gray-400 border-gray-500/20' }
    };
    return statusMap[status] || statusMap['open'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className={`mt-4 ${subTextClass}`}>Cargando...</p>
        </div>
      </div>
    );
  }

  // Mostrar error si existe
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className={`text-xl font-bold ${textClass} mb-2`}>Error al cargar datos</h2>
          <p className={`${subTextClass} mb-4`}>{error}</p>
          <button
            onClick={() => loadUserData()}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con bienvenida */}
      <div>
        <h1 className={`text-3xl font-bold ${textClass}`}>
          {isTechnician ? '👨‍🔧 Panel de Técnico' : '👋 Bienvenido'}, {user?.name}
        </h1>
        <p className={`${subTextClass} mt-1`}>
          {isTechnician 
            ? 'Gestiona todos los tickets del sistema' 
            : 'Aquí puedes ver tus tickets y obtener soporte'}
        </p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className={`${cardClass} border rounded-xl p-6 transition-all duration-200 hover:shadow-lg ${stat.borderColor}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${subTextClass}`}>{stat.title}</p>
                <p className={`text-3xl font-bold ${textClass} mt-2`}>{stat.value}</p>
              </div>
              <div className={`${stat.bgColor} p-3 rounded-lg`}>
                <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Acciones Rápidas */}
      <div>
        <h2 className={`text-xl font-semibold ${textClass} mb-4`}>Acciones Rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`${action.bgColor} ${action.hoverColor} text-white p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-3`}
            >
              <action.icon className="w-5 h-5" />
              <span className="font-medium">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tickets Recientes */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-semibold ${textClass}`}>
            {isTechnician ? 'Tickets Recientes' : 'Mis Tickets Recientes'}
          </h2>
          <button
            onClick={() => navigate('/dashboard/tickets')}
            className={`text-blue-500 hover:text-blue-600 flex items-center space-x-1 text-sm font-medium`}
          >
            <span>Ver todos</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className={`${cardClass} border rounded-xl divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {recentTickets.length === 0 ? (
            <div className="p-12 text-center">
              <TicketIcon className={`w-16 h-16 ${isDark ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
              <p className={`text-lg font-medium ${textClass} mb-2`}>
                No hay tickets
              </p>
              <p className={subTextClass}>
                {isTechnician ? 'No hay tickets en el sistema' : 'Crea tu primer ticket para obtener soporte'}
              </p>
              {!isTechnician && (
                <button
                  onClick={() => navigate('/dashboard/tickets/create')}
                  className="mt-4 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
                >
                  Crear Ticket
                </button>
              )}
            </div>
          ) : (
            recentTickets.map((ticket, index) => {
              const statusBadge = getStatusBadge(ticket.status);
              return (
                <div
                  key={ticket.id || index}
                  onClick={() => navigate(`/dashboard/tickets/${ticket.id}`)}
                  className={`p-4 ${isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-sm font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          #{ticket.id}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-md border ${statusBadge.class}`}>
                          {statusBadge.label}
                        </span>
                      </div>
                      <p className={`font-medium ${textClass} truncate`}>{ticket.title}</p>
                      <p className={`text-sm ${subTextClass} mt-1 line-clamp-1`}>
                        {ticket.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className={subTextClass}>
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                        {ticket.assigned_to && (
                          <span className={subTextClass}>
                            <User className="w-3 h-3 inline mr-1" />
                            {ticket.assigned_to}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 ${subTextClass} flex-shrink-0 ml-2`} />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;